import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { i18n } from "../../translate/i18n";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import axios from "axios";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    paddingBottom: 80,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  infoCard: {
    borderRadius: 10,
    border: "1px solid rgba(234,179,8,0.30)",
    backgroundColor: "rgba(234,179,8,0.05)",
    padding: "14px 18px",
    marginBottom: theme.spacing(3),
  },
  infoTitle: {
    color: "#ca8a04",
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  infoText: {
    color: theme.palette.text.primary,
    fontSize: 13.5,
    lineHeight: 1.6,
    marginBottom: 6,
  },
  numberFormat: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    flexWrap: "wrap",
  },
  numberChip: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    borderRadius: 6,
    padding: "3px 10px",
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  sectionCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)"}`,
    overflow: "hidden",
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f1f5f9",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)"}`,
  },
  sectionBadge: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  sectionBody: {
    padding: theme.spacing(2.5),
  },
  sectionDesc: {
    fontSize: 13.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  codeBlock: {
    backgroundColor: "#130a22",
    borderRadius: 8,
    padding: "16px 18px",
    fontFamily: '"Fira Code", "Cascadia Code", "Courier New", monospace',
    fontSize: 12.5,
    lineHeight: 1.9,
    overflowX: "auto",
  },
  codeTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  methodPill: {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "2px 10px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "monospace",
    letterSpacing: "0.06em",
    flexShrink: 0,
  },
  endpointUrl: {
    color: "#7dd3fc",
    fontSize: 12,
    wordBreak: "break-all",
  },
  codeSeparator: {
    borderTop: "1px solid rgba(255,255,255,0.08)",
    margin: "10px 0",
  },
  codeGroupLabel: {
    color: "#64748b",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 4,
  },
  codeRow: {
    display: "flex",
    gap: 12,
    paddingLeft: 4,
  },
  codeKey: {
    color: "#c4b5fd",
    minWidth: 148,
    flexShrink: 0,
  },
  codeVal: {
    color: "#86efac",
  },
  testLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
  },
  formGrid: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  fileInputWrapper: {
    border: `1px dashed ${theme.palette.type === "dark" ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.18)"}`,
    borderRadius: 8,
    padding: "10px 14px",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: theme.palette.text.secondary,
    fontSize: 13,
    display: "block",
  },
  submitRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  submitBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    minWidth: 110,
  },
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [formMessageTextData] = useState({
    token: "",
    number: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [formMessageMediaData] = useState({
    token: "",
    number: "",
    medias: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [file, setFile] = useState({});
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error("Tu empresa no tiene acceso a esta página.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () =>
    process.env.REACT_APP_BACKEND_URL + "/api/messages/send";

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    try {
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data: { number, body, userId, queueId },
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messagesAPI.toasts.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append("number", values.number);
      data.append("body", values.body ? values.body : firstFile.name);
      data.append("userId", values.userId);
      data.append("queueId", values.queueId);
      data.append("medias", firstFile);
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "multipart/form-data",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messagesAPI.toasts.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const textFields = [
    { name: "token", label: i18n.t("messagesAPI.textMessage.token"), required: true, md: 6 },
    { name: "number", label: i18n.t("messagesAPI.textMessage.number"), required: true, md: 6 },
    { name: "body", label: i18n.t("messagesAPI.textMessage.body"), required: true, md: 12 },
    { name: "userId", label: i18n.t("messagesAPI.textMessage.userId"), required: false, md: 6 },
    { name: "queueId", label: i18n.t("messagesAPI.textMessage.queueId"), required: false, md: 6 },
  ];

  const mediaFields = [
    { name: "token", label: i18n.t("messagesAPI.mediaMessage.token"), required: true, md: 6 },
    { name: "number", label: i18n.t("messagesAPI.mediaMessage.number"), required: true, md: 6 },
    { name: "body", label: i18n.t("messagesAPI.textMessage.body"), required: false, md: 12 },
    { name: "userId", label: i18n.t("messagesAPI.textMessage.userId"), required: false, md: 6 },
    { name: "queueId", label: i18n.t("messagesAPI.textMessage.queueId"), required: false, md: 6 },
  ];

  const renderForm = (fields, initialValues, onSubmit, isMedia = false) => (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, actions) => {
        setTimeout(async () => {
          await onSubmit(values);
          actions.setSubmitting(false);
          actions.resetForm();
          if (isMedia) {
            const el = document.getElementById("medias");
            if (el) { el.files = null; el.value = null; }
          }
        }, 400);
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={1} className={classes.formGrid}>
            {fields.map(({ name, label, required, md }) => (
              <Grid item xs={12} md={md} key={name}>
                <Field
                  as={TextField}
                  label={label}
                  name={name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required={required}
                />
              </Grid>
            ))}
            {isMedia && (
              <Grid item xs={12}>
                <input
                  type="file"
                  name="medias"
                  id="medias"
                  required
                  className={classes.fileInputWrapper}
                  onChange={(e) => setFile(e.target.files)}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <div className={classes.submitRow}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  className={classes.submitBtn}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} style={{ color: "#fff" }} />
                  ) : (
                    i18n.t("messagesAPI.toSend")
                  )}
                </Button>
              </div>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const CodeBlock = ({ fields, contentType }) => (
    <div className={classes.codeBlock}>
      <div className={classes.codeTopRow}>
        <span className={classes.methodPill}>POST</span>
        <span className={classes.endpointUrl}>{getEndpoint()}</span>
      </div>
      <div className={classes.codeSeparator} />
      <div className={classes.codeGroupLabel}>Headers</div>
      <div className={classes.codeRow}>
        <span className={classes.codeKey}>Authorization</span>
        <span className={classes.codeVal}>Bearer &#123;token&#125;</span>
      </div>
      <div className={classes.codeRow}>
        <span className={classes.codeKey}>Content-Type</span>
        <span className={classes.codeVal}>{contentType}</span>
      </div>
      <div className={classes.codeSeparator} />
      <div className={classes.codeGroupLabel}>{contentType === "application/json" ? "Body (JSON)" : "FormData"}</div>
      {fields.map(([key, val]) => (
        <div key={key} className={classes.codeRow}>
          <span className={classes.codeKey}>{key}</span>
          <span className={classes.codeVal}>{val}</span>
        </div>
      ))}
    </div>
  );

  const textBodyFields = [
    ['"number"', '"558599999999"'],
    ['"body"', '"Mensaje de texto"'],
    ['"userId"', 'ID de usuario  (opcional)'],
    ['"queueId"', 'ID del Departamento  (opcional)'],
    ['"sendSignature"', 'true / false'],
    ['"closeTicket"', 'true / false'],
  ];

  const mediaBodyFields = [
    ['"number"', '"558599999999"'],
    ['"body"', '"Nombre o descripción"  (opcional)'],
    ['"userId"', 'ID de usuario  (opcional)'],
    ['"queueId"', 'ID del Departamento  (opcional)'],
    ['"medias"', 'archivo adjunto'],
    ['"sendSignature"', 'true / false'],
    ['"closeTicket"', 'true / false'],
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("messagesAPI.API.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">

        {/* Info card */}
        <div className={classes.infoCard}>
          <div className={classes.infoTitle}>
            ⚠ {i18n.t("messagesAPI.API.instructions.comments")}
          </div>
          <Typography className={classes.infoText}>
            Para enviar mensajes necesitas registrar un token en la conexión que los enviará.
            Ve a <b>Conexiones</b>, edita la conexión y copia el token en el campo correspondiente.
          </Typography>
          <Typography className={classes.infoText} style={{ marginBottom: 4 }}>
            {i18n.t("messagesAPI.API.instructions.comments2")}
          </Typography>
          <div className={classes.numberFormat}>
            <span className={classes.numberChip}>{i18n.t("messagesAPI.API.instructions.codeCountry")}</span>
            <span style={{ color: theme.palette.text.secondary, fontSize: 13 }}>+</span>
            <span className={classes.numberChip}>{i18n.t("messagesAPI.API.instructions.code")}</span>
            <span style={{ color: theme.palette.text.secondary, fontSize: 13 }}>+</span>
            <span className={classes.numberChip}>{i18n.t("messagesAPI.API.instructions.number")}</span>
            <span style={{ color: theme.palette.text.secondary, fontSize: 12, marginLeft: 4 }}>
              Ej: 528112345678
            </span>
          </div>
        </div>

        {/* Section 1 — Texto */}
        <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionBadge}>1</div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("messagesAPI.API.methods.messagesText")}
            </Typography>
          </div>
          <div className={classes.sectionBody}>
            <Typography className={classes.sectionDesc}>
              {i18n.t("messagesAPI.API.text.instructions")}
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} md={6}>
                <CodeBlock fields={textBodyFields} contentType="application/json" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className={classes.testLabel}>
                  {i18n.t("messagesAPIInstructions.testSend")}
                </Typography>
                {renderForm(textFields, formMessageTextData, handleSendTextMessage)}
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Section 2 — Archivos */}
        <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionBadge}>2</div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("messagesAPI.API.methods.messagesMidia")}
            </Typography>
          </div>
          <div className={classes.sectionBody}>
            <Typography className={classes.sectionDesc}>
              {i18n.t("messagesAPI.API.media.instructions")}
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} md={6}>
                <CodeBlock fields={mediaBodyFields} contentType="multipart/form-data" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className={classes.testLabel}>
                  {i18n.t("messagesAPIInstructions.testSend")}
                </Typography>
                {renderForm(mediaFields, formMessageMediaData, handleSendMediaMessage, true)}
              </Grid>
            </Grid>
          </div>
        </div>

      </Paper>
    </MainContainer>
  );
};

export default MessagesAPI;
